import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Heart, ShoppingBag, User, Menu, X, ChevronDown, Facebook, Twitter, Instagram, Linkedin, Youtube } from 'lucide-react';
import AnalyticsManager, { trackPageView } from '../AnalyticsManager';

const API = process.env.REACT_APP_BACKEND_URL;

function ShopLayout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const customerToken = localStorage.getItem('b2c_customer_token');
    setIsLoggedIn(!!customerToken);
    fetchSiteSettings();
  }, []);

  // Track page views on route change
  useEffect(() => {
    trackPageView(document.title, window.location.href);
  }, [location.pathname]);

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/b2c/cms/site-settings`);
      setSiteSettings(response.data);
    } catch (error) {
      console.error('Error fetching site settings:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const navLinks = [
    { name: 'Flash Sale', path: '/shop/flash-sale' },
    { name: 'Buy Mobiles', path: '/shop/buy-mobile' },
    { name: 'Buy Laptops', path: '/shop/buy-laptop' },
    { name: 'Why Refurbished', path: '/shop/why-refurbished' },
    { name: 'Wholesale (B2B)', path: '/shop/wholesale' },
    { name: 'Channel Partner', path: '/shop/franchise' },
  ];

  // Default values if settings not loaded
  const settings = siteSettings || {
    tagline: 'खुशियों वाला BOX',
    phone: '+91 91111 77118',
    whatsapp: '+91 91111 77118',
    email: 'support@zobox.in',
    address_registered: '4th Floor, 6/77, WEA, Karol Bagh, New Delhi - 110005, India',
    address_corporate: 'E218C, Sector - 63, Noida, Uttar Pradesh - 201301, India',
    footer_disclaimer: 'All product names, logos, and trademarks are the property of their respective owners.',
    security_notice: 'ZOBOX employees will never ask for your OTP over phone or email.'
  };

  // Extract WhatsApp number for link
  const whatsappNumber = (settings.whatsapp || '').replace(/[^0-9]/g, '');

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Analytics & Tracking Pixels */}
      <AnalyticsManager settings={siteSettings} />

      {/* Main Header - Zobox Style */}
      <header className="bg-[#2B3380] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/shop" className="flex-shrink-0">
              {settings.logo_url ? (
                <img src={settings.logo_url} alt="Zobox" className="h-10 w-auto" />
              ) : (
                <div className="flex items-center gap-1">
                  <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                    <span className="text-[#2B3380] font-bold text-lg">Z</span>
                  </div>
                  <span className="text-white font-bold text-xl tracking-wide">OBOX</span>
                </div>
              )}
            </Link>

            {/* Search Bar - Desktop */}
            <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-lg mx-8">
              <div className="relative w-full">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search"
                  className="w-full px-4 py-2.5 bg-white border-2 border-white rounded-lg text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-300"
                />
                <button
                  type="submit"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-[#2B3380]"
                >
                  <Search className="w-5 h-5" />
                </button>
              </div>
            </form>

            {/* Right Icons */}
            <div className="flex items-center gap-4">
              {/* Mobile Search Toggle */}
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="md:hidden text-white hover:text-gray-200"
              >
                <Search className="w-6 h-6" />
              </button>
              
              <Link to="/shop/wishlist" className="text-white hover:text-gray-200 relative">
                <Heart className="w-6 h-6" />
                {wishlistCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {wishlistCount}
                  </span>
                )}
              </Link>
              
              <Link to="/shop/cart" className="text-white hover:text-gray-200 relative">
                <ShoppingBag className="w-6 h-6" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {cartCount}
                  </span>
                )}
              </Link>
              
              <Link to={isLoggedIn ? '/shop/account' : '/shop/login'} className="text-white hover:text-gray-200">
                <User className="w-6 h-6" />
              </Link>
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden text-white"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Search */}
          {showSearch && (
            <form onSubmit={handleSearch} className="md:hidden pb-3">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search"
                  className="w-full px-4 py-2.5 bg-white rounded-lg text-gray-800"
                  autoFocus
                />
                <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                  <Search className="w-5 h-5" />
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Navigation - Desktop */}
        <nav className="hidden md:block bg-white border-b">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center gap-8 py-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-sm font-medium transition ${
                    location.pathname === link.path
                      ? 'text-[#2B3380]'
                      : 'text-gray-700 hover:text-[#2B3380]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <button className="text-sm font-medium text-gray-700 hover:text-[#2B3380] flex items-center gap-1">
                More <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block py-3 text-sm font-medium border-b border-gray-100 ${
                    location.pathname === link.path ? 'text-[#2B3380]' : 'text-gray-700'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* WhatsApp Floating Button */}
      <a
        href={`https://wa.me/${whatsappNumber || '919111177118'}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transition-transform hover:scale-110"
        data-testid="whatsapp-btn"
      >
        <svg viewBox="0 0 24 24" className="w-8 h-8 text-white fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* Footer - Zobox Style */}
      <footer className="bg-gray-50 border-t">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
            {/* Logo & Chat */}
            <div className="col-span-2 md:col-span-1">
              <Link to="/shop" className="flex items-center gap-1 mb-4">
                {settings.logo_url ? (
                  <img src={settings.logo_url} alt="Zobox" className="h-10 w-auto" />
                ) : (
                  <>
                    <div className="w-8 h-8 bg-[#2B3380] rounded flex items-center justify-center">
                      <span className="text-white font-bold text-lg">Z</span>
                    </div>
                    <span className="text-[#2B3380] font-bold text-xl">OBOX</span>
                  </>
                )}
              </Link>
              <p className="text-gray-500 text-sm mb-4">
                {settings.tagline}
              </p>
              <a
                href={`https://wa.me/${whatsappNumber || '919111177118'}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#2B3380] text-white rounded-full text-sm font-medium hover:bg-[#1e2563] transition"
              >
                Chat With Us
              </a>
              {/* Social Links */}
              <div className="flex gap-3 mt-4">
                {settings.social_facebook && (
                  <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#2B3380]">
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {settings.social_twitter && (
                  <a href={settings.social_twitter} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#2B3380]">
                    <Twitter className="w-5 h-5" />
                  </a>
                )}
                {settings.social_instagram && (
                  <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#2B3380]">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
                {settings.social_linkedin && (
                  <a href={settings.social_linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#2B3380]">
                    <Linkedin className="w-5 h-5" />
                  </a>
                )}
                {settings.social_youtube && (
                  <a href={settings.social_youtube} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-[#2B3380]">
                    <Youtube className="w-5 h-5" />
                  </a>
                )}
                {/* Show placeholders if no social links configured */}
                {!settings.social_facebook && !settings.social_twitter && !settings.social_instagram && !settings.social_linkedin && !settings.social_youtube && (
                  <>
                    <span className="w-5 h-5 bg-gray-200 rounded-full"></span>
                    <span className="w-5 h-5 bg-gray-200 rounded-full"></span>
                    <span className="w-5 h-5 bg-gray-200 rounded-full"></span>
                    <span className="w-5 h-5 bg-gray-200 rounded-full"></span>
                  </>
                )}
              </div>
            </div>

            {/* Shop */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Shop</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/shop/buy-mobile" className="text-gray-600 hover:text-[#2B3380]">Buy Phones</Link></li>
                <li><Link to="/shop/buy-laptop" className="text-gray-600 hover:text-[#2B3380]">Buy Laptops</Link></li>
                <li><Link to="/shop/track-order" className="text-gray-600 hover:text-[#2B3380]">Track Order</Link></li>
                <li><Link to="/shop/franchise" className="text-gray-600 hover:text-[#2B3380]">Become a Partner</Link></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/shop/about" className="text-gray-600 hover:text-[#2B3380]">About Us</Link></li>
                <li><Link to="/shop/news" className="text-gray-600 hover:text-[#2B3380]">Press Release</Link></li>
                <li><Link to="/shop/careers" className="text-gray-600 hover:text-[#2B3380]">Careers</Link></li>
                <li><Link to="/shop/blog" className="text-gray-600 hover:text-[#2B3380]">Blog</Link></li>
              </ul>
            </div>

            {/* Policies */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Policies</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/shop/policy/terms" className="text-gray-600 hover:text-[#2B3380]">Terms & Conditions</Link></li>
                <li><Link to="/shop/contact" className="text-gray-600 hover:text-[#2B3380]">Contact Us</Link></li>
                <li><Link to="/shop/policy/faqs" className="text-gray-600 hover:text-[#2B3380]">FAQ</Link></li>
                <li><Link to="/shop/policy/shipping-policy" className="text-gray-600 hover:text-[#2B3380]">Shipping Policy</Link></li>
                <li><Link to="/shop/policy/return-policy" className="text-gray-600 hover:text-[#2B3380]">Return & Refund Policy</Link></li>
                <li><Link to="/shop/policy/warranty-policy" className="text-gray-600 hover:text-[#2B3380]">Warranty Policy</Link></li>
                <li><Link to="/shop/policy/privacy" className="text-gray-600 hover:text-[#2B3380]">Privacy Policy</Link></li>
              </ul>
            </div>

            {/* Brands */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Brands</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/shop/buy-mobile?brand=apple" className="text-gray-600 hover:text-[#2B3380]">Apple</Link></li>
                <li><Link to="/shop/buy-mobile?brand=samsung" className="text-gray-600 hover:text-[#2B3380]">Samsung</Link></li>
                <li><Link to="/shop/buy-mobile?brand=mi" className="text-gray-600 hover:text-[#2B3380]">MI</Link></li>
                <li><Link to="/shop/buy-mobile?brand=vivo" className="text-gray-600 hover:text-[#2B3380]">VIVO</Link></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                {settings.phone && (
                  <li>
                    <a href={`tel:${settings.phone.replace(/[^0-9+]/g, '')}`} className="hover:text-[#2B3380]">
                      {settings.phone}
                    </a>
                  </li>
                )}
                {settings.email && (
                  <li>
                    <a href={`mailto:${settings.email}`} className="hover:text-[#2B3380]">
                      {settings.email}
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Footer */}
        <div className="border-t bg-white">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="text-sm text-gray-500 space-y-2">
              {settings.address_registered && (
                <p><strong>Registered Address:</strong> Zobox Protech Limited. | {settings.address_registered}</p>
              )}
              {settings.address_corporate && (
                <p><strong>Corporate Address:</strong> Zobox Protech Limited. | {settings.address_corporate}</p>
              )}
              {settings.footer_disclaimer && (
                <p className="mt-4 text-xs">{settings.footer_disclaimer}</p>
              )}
              {settings.security_notice && (
                <p className="text-xs"><strong>Important Security Notice:</strong> {settings.security_notice}</p>
              )}
            </div>
            <div className="mt-6 pt-6 border-t text-center text-sm text-gray-500">
              Copyright @ {new Date().getFullYear()} Zobox. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default ShopLayout;
